/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect } from "react";

// @mui material components
import { useTheme, useMediaQuery } from "@mui/material";

// react-router-dom components
import { useLocation, NavLink, useNavigate } from "react-router-dom";

// Authentication context
import { useAuth } from "context/AuthContext";

// prop-types is a library for typechecking of props.
import PropTypes from "prop-types";

// @mui material components
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import SidenavCollapse from "examples/Sidenav/SidenavCollapse";

// Custom styles for the Sidenav
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";
import brandImg from "assets/images/NAMUVE LOGO-03.png";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
} from "context";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode, sidenavColor } = controller;
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const collapseName = location.pathname.replace("/", "");

  // Check if screen is mobile (below 1200px)
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(1200));


  // Handle logout functionality with proper navigation
  const handleLogout = () => {
    logout();
    // Use replace to prevent back button issues
    navigate("/authentication/sign-in", { replace: true });
  };

  let textColor = "white";

  if (transparentSidenav || (whiteSidenav && !darkMode)) {
    textColor = "dark";
  } else if (whiteSidenav && darkMode) {
    textColor = "inherit";
  }

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  useEffect(() => {
    // Set initial state on first load based on window width
    setMiniSidenav(dispatch, window.innerWidth < 1200);
  }, []);

  useEffect(() => {
    function handleResize() {
      setTransparentSidenav(dispatch, window.innerWidth < 1200 ? false : transparentSidenav);
      setWhiteSidenav(dispatch, window.innerWidth < 1200 ? false : whiteSidenav);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [dispatch]);

  // Render all the routes from the routes.js (All the visible items on the Sidenav)
  const renderRoutes = routes.map(({ type, name, icon, title, noCollapse, key, href, route }) => {
    let returnValue;

    if (type === "collapse") {
      // Handle logout functionality
      if (key === "logout") {
        returnValue = (
          <div key={key} onClick={handleLogout} style={{ cursor: "pointer" }}>
            <SidenavCollapse name={name} icon={icon} active={false} />
          </div>
        );
      } else if (href) {
        returnValue = (
          <Link
            href={href}
            key={key}
            target="_blank"
            rel="noreferrer"
            sx={{ textDecoration: "none" }}
          >
            <SidenavCollapse
              name={name}
              icon={icon}
              active={key === collapseName}
              noCollapse={noCollapse}
            />
          </Link>
        );
      } else {
        returnValue = (
          <NavLink key={key} to={route}>
            <SidenavCollapse name={name} icon={icon} active={key === collapseName} />
          </NavLink>
        );
      }
    } else if (type === "title") {
      returnValue = (
        <MDTypography
          key={key}
          color={textColor}
          display={miniSidenav ? "none" : "block"}
          variant="caption"
          fontWeight="bold"
          textTransform="uppercase"
          pl={3}
          mt={2}
          mb={1}
          ml={1}
        >
          {title}
        </MDTypography>
      );
    } else if (type === "divider") {
      returnValue = (
        <Divider
          key={key}
          light={
            (!darkMode && !whiteSidenav && !transparentSidenav) ||
            (darkMode && !transparentSidenav && whiteSidenav)
          }
        />
      );
    }

    return returnValue;
  });

  const { palette } = theme;
  let logoBgColor = darkMode ? (palette.background.sidenav || "#1f283e") : "#5e6b47";
  if (transparentSidenav) {
    logoBgColor = palette.transparent.main;
  } else if (whiteSidenav) {
    logoBgColor = palette.white.main;
  }

  return (
    <SidenavRoot
      {...rest}
      variant={isMobile ? "temporary" : "permanent"}
      open={isMobile ? !miniSidenav : true}
      onClose={() => setMiniSidenav(dispatch, true)}
      ModalProps={{
        keepMounted: true, // Better open performance on mobile
        disableAutoFocus: true,
        disableEnforceFocus: true,
        BackdropProps: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
        }
      }}
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode }}
    >
      <MDBox
        onClick={() => setMiniSidenav(dispatch, !miniSidenav)}
        display={isMobile ? "none" : "flex"}
        alignItems="center"
        justifyContent="center"
        position="absolute"
        top="50%"
        right="-12px"
        width="24px"
        height="24px"
        borderRadius="50%"
        bgcolor={darkMode ? "#344767" : "#5e6b47"}
        color="#fff"
        sx={{
          cursor: "pointer",
          zIndex: 1400,
          border: "1.5px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          transition: "all 0.2s ease",
          transform: "translateY(-50%)",
          "&:hover": {
            transform: "translateY(-50%) scale(1.1)",
            bgcolor: darkMode ? "#3d5277" : "#6c7b52",
          },
        }}
      >
        <Icon sx={{ fontWeight: "bold", fontSize: "16px !important" }}>
          {miniSidenav ? "chevron_right" : "chevron_left"}
        </Icon>
      </MDBox>
      <MDBox
        pt={0}
        pb={0}
        px={2}
        textAlign="center"
        sx={{
          display: miniSidenav ? "none" : "block",
          width: "100%",
          bgcolor: logoBgColor,
          zIndex: 1400,
          position: "relative",
        }}
      >
        <MDBox
          display={{ xs: "block", xl: "none" }}
          position="absolute"
          top={0}
          right={0}
          p={1.625}
          onClick={closeSidenav}
          sx={{ cursor: "pointer" }}
        >
          <MDTypography variant="h6" color="secondary">
            <Icon sx={{ fontWeight: "bold" }}>close</Icon>
          </MDTypography>
        </MDBox>
        <MDBox
          component={NavLink}
          to="/"
          display="flex"
          alignItems="center"
          justifyContent="flex-start"
          sx={{ width: "100%", overflow: "visible" }}
        >
          <MDBox
            component="img"
            src={brandImg}
            alt="Brand"
            sx={{
              borderRadius: "0px",
              padding: "0px",
              objectFit: "contain",
              mt: -3,
              mb: -3,
              width: "218px",
              minWidth: "218px",
              transition: "all 0.2s ease",
            }}
          />
        </MDBox>
      </MDBox>
      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
        sx={{ 
          mt: 0, 
          mb: 1,
          display: miniSidenav ? "none" : "block"
        }}
      />
      <List>{renderRoutes}</List>
    </SidenavRoot>
  );
}

// Setting default values for the props of Sidenav
Sidenav.defaultProps = {
  color: "info",
  brand: "",
};

// Typechecking props for the Sidenav
Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
